import type { ProtocolIssue } from "./protocolGrouping";

type Side = NonNullable<ProtocolIssue["left"]>;

export type ProtocolPresentation = {
  title: string;
  leftCaption: string;
  rightCaption: string;
  action: string | null;
  limitation: string | null;
};

const normalizeLabel = (label: string) => label.toLocaleLowerCase("ru-RU").replace(/\s+/gu, " ").trim();

const normalizeSearch = (text: string) => normalizeLabel(text)
  .replace(/(?<=\d),(?=\d)/gu, ".")
  .replace(/(?<=\d)\s+(?=\d)/gu, "");

export function protocolMatchesSearch(issue: ProtocolIssue, query: string): boolean {
  return normalizeSearch([
    issue.work, issue.reason, issue.group, issue.scope, issue.position, issue.building,
    ...(issue.places || []), issue.formula, issue.left?.label, issue.right?.label,
    issue.left?.value, issue.right?.value, issue.left?.unit, issue.right?.unit,
    ...(issue.left?.addresses || []).map(address => address.record_key),
    ...(issue.right?.addresses || []).map(address => address.record_key),
  ].join(" ")).includes(normalizeSearch(query));
}

function hasVisibleMarks(side: Side): boolean {
  return side.role === "plan" && /видимых марок|видимые марки/u.test(normalizeLabel(side.label));
}

function caption(side: Side | null | undefined): string {
  if (!side) return "";
  switch (side.role) {
    case "specification": {
      const label = normalizeLabel(side.label);
      const original = label.includes("исходное количество");
      const alternative = label.includes("альтернативное количество");
      // These are labels of observations, never old/new revisions or approved edits.
      if (original && !alternative) return "Спецификация · исходное";
      if (alternative && !original) return "Спецификация · альтернативное";
      return "По спецификации";
    }
    case "plan": return hasVisibleMarks(side) ? "Найдено на планах" : "По планам";
    case "vor": return "По ВОР";
    case "cable_journal": return "По кабельному журналу";
    case "technical": return "По техническим данным";
    default: return "По источнику";
  }
}

// Explicit phrases emitted by opening_counts.py and eom_checks.py. Do not infer
// completeness from role, address precision, measurement IDs or verification defaults.
const COMPLETENESS_WARNINGS = [
  "Общие выноски и полнота обозначений требуют проверки",
  "Есть непрочитанные планы",
  "Листы без явного этажа не входят",
  "Распознавание символов неполное: найденное — нижняя граница, не точное количество",
  "Не все планы раздела проиндексированы; количество не установлено",
];

function completenessWarning(sides: readonly Side[]): string | null {
  const labels = sides.map(side => normalizeLabel(side.label));
  const warnings = COMPLETENESS_WARNINGS.filter(warning => labels.some(label => label.includes(normalizeLabel(warning))));
  return warnings.length ? warnings.join(". ") : null;
}

/** Presentation only: preserve the original issue, side order, evidence and decision identity. */
export function protocolPresentation(issue: ProtocolIssue): ProtocolPresentation {
  const { left, right } = issue;
  const sides = [left, right].filter((side): side is Side => side != null);
  const result: ProtocolPresentation = {
    title: issue.reason,
    leftCaption: caption(left),
    rightCaption: caption(right),
    action: null,
    limitation: completenessWarning(sides),
  };

  switch (issue.kind) {
    case "incomplete": result.action = "Уточнить недостающие данные"; return result;
    case "units": result.action = "Проверить единицы измерения"; return result;
    case "recognition": result.action = "Проверить распознавание по источнику"; return result;
    case "unread_document": result.action = "Проверить исходный документ"; return result;
    case "zero_base": result.action = "Проверить нулевое значение"; return result;
    case "quantity": break;
    default: return result;
  }

  if (!left || !right) {
    result.action = "Проверить основание замечания";
    return result;
  }
  if (left.value == null || right.value == null) {
    result.action = "Уточнить недостающие данные";
    return result;
  }
  if (left.role === "specification" && right.role === "specification") {
    const sameSource = left.source_ids.length === 1 && right.source_ids.length === 1 &&
      left.source_ids[0].trim() !== "" && left.source_ids[0] === right.source_ids[0];
    result.title = sameSource ? "Два значения в спецификации" : "Сверка значений спецификаций";
    result.action = "Уточнить актуальное количество";
  } else if ((left.role === "specification" && right.role === "plan") ||
    (left.role === "plan" && right.role === "specification")) {
    result.title = "Сверка спецификации с планами";
    result.action = sides.some(hasVisibleMarks) ? "Проверить полноту подсчёта" : "Сопоставить основания";
  } else {
    result.action = "Сопоставить основания";
  }
  return result;
}
